<script setup lang="ts">
  import { computed } from 'vue'
  import { CircleHelp } from 'lucide-vue-next'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { useTranslation } from '@/composables/useTranslation'
  import { useMetrics } from '@/composables/useMetrics'
  import { useLocaleStore } from '@/store/localStore'
  import { EventNameEnum } from '@/types/generated'

  interface Props {
    /** The knowledge base URL to open */
    url: string
    /** Optional custom tooltip text. Defaults to "Learn more" */
    tooltip?: string
    /** Optional location identifier for analytics tracking */
    location?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    tooltip: undefined,
    location: undefined
  })

  const { t } = useTranslation({
    en: {
      learnMore: 'Learn more'
    },
    de: {
      learnMore: 'Mehr erfahren'
    },
    // The icon is hidden when locale = 'fr' (see hideForCurrentLocale below) so
    // this stub is never displayed. It exists to keep the message-key set in
    // parity across locales — useTranslation flags missing keys, and a future
    // ticket can drop both this block and the hideForCurrentLocale gate when
    // a French knowledge base is wired up.
    fr: {
      learnMore: 'En savoir plus'
    }
  })

  const { trackEvent } = useMetrics()

  // FL-005: hide the help icon for French users until KNOWLEDGE_BASE_URLS gain
  // a French translation. Today every URL points at hilfe.jupus.de (German-only
  // Hubspot KB), so showing the icon to a French recipient would land them on
  // a German article — flagged as broken UX. Gated here rather than at the
  // 6 call sites so the rule lives in one place.
  const localeStore = useLocaleStore()
  const hideForCurrentLocale = computed(() => localeStore.locale === 'fr')

  const tooltipText = computed(() => props.tooltip || t('learnMore'))

  const handleClick = async () => {
    // Track analytics event
    try {
      await trackEvent(EventNameEnum.HELP_LINK_CLICKED, {
        help_link_url: props.url,
        help_link_location: props.location || 'unknown'
      })
    } catch (error) {
      // Silently fail analytics tracking - don't block user action
      console.error('Failed to track help link click:', error)
    }
  }
</script>

<template>
  <TooltipProvider v-if="!hideForCurrentLocale">
    <Tooltip>
      <TooltipTrigger as-child>
        <a
          :href="url"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="tooltipText"
          class="inline-flex items-center rounded text-gray-500 transition-colors hover:text-gray-700
            focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          @click="handleClick"
        >
          <CircleHelp class="h-5 w-5" />
        </a>
      </TooltipTrigger>
      <TooltipContent variant="secondary">
        <p>{{ tooltipText }}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
